/**
 * VivaDecor – discounted add-on decor on 3D background product pages.
 *
 * Accordion (only one row open at a time), quantity stepper per add-on (0–20),
 * live summary "Background X + Decor Y = Total Z" and the item count on the Add to cart button.
 * The actual cart request is made by product-form.js, which calls getCartItems() / createBundleId() / reset().
 * Prices come from Liquid (current presentment currency, in minor units); they are never hardcoded here.
 */

const VD_MIN_QTY = 0;
const VD_MAX_QTY = 20;

/**
 * Builds a money formatter from a sample rendered by Liquid's money filter (123456789 minor units),
 * so the output always matches the shop's money format of the current currency.
 * @param {string} sample
 * @returns {(cents: number) => string}
 */
function vdMoneyFormatter(sample) {
  const match = (sample || '').match(/\d[\d.,'\s  ]*\d/);

  if (!match) {
    return (cents) => (cents / 100).toFixed(2);
  }

  const number = match[0];
  const prefix = sample.slice(0, match.index);
  const suffix = sample.slice((match.index || 0) + number.length);
  const separators = number.match(/[^\d]/g) || [];
  const groups = number.split(/[^\d]/);
  const lastGroup = groups[groups.length - 1] || '';

  let decimals = 0;
  let decimalSeparator = '';
  let thousandsSeparator = '';

  if (groups.length > 1 && lastGroup.length === 2) {
    decimals = 2;
    decimalSeparator = separators[separators.length - 1] || '.';
    thousandsSeparator = separators.length > 1 ? separators[0] || '' : '';
  } else {
    thousandsSeparator = separators[0] || '';
  }

  return (cents) => {
    const fixed = (Math.round(cents) / 100).toFixed(decimals);
    const parts = fixed.split('.');
    const whole = (parts[0] || '0').replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

    return prefix + (decimals > 0 ? whole + decimalSeparator + (parts[1] || '00') : whole) + suffix;
  };
}

class VdAddons extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Record<string, number>} */
  #variantPrices = {};

  /** @type {(cents: number) => string} */
  #formatMoney = (cents) => (cents / 100).toFixed(2);

  /** @type {number | undefined} */
  #labelTimeout;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    try {
      const pricesScript = this.querySelector('script[data-vd-variant-prices]');
      this.#variantPrices = JSON.parse(pricesScript?.textContent || '{}');
    } catch (error) {
      this.#variantPrices = {};
    }

    this.#formatMoney = vdMoneyFormatter(this.dataset.moneySample || '');

    this.addEventListener('click', this.#onClick, { signal });

    // Quantity of the background (theme quantity selector)
    document.addEventListener('quantity-selector:update', this.#onQuantitySelectorUpdate, { signal });
    this.#productForm?.addEventListener('change', this.#onFormChange, { signal });
    this.#productForm?.addEventListener('input', this.#onFormChange, { signal });

    // Variant change of the background: price changes, the theme re-renders the Add to cart button
    const section = this.closest('.shopify-section, dialog, product-card');
    section?.addEventListener('variant:update', this.#onVariantUpdate, { signal });

    this.#render();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    if (this.#labelTimeout) clearTimeout(this.#labelTimeout);
  }

  /** @returns {HTMLElement | null} */
  get #productForm() {
    return this.closest('product-form-component');
  }

  /** @returns {HTMLElement[]} */
  get #items() {
    return Array.from(this.querySelectorAll('[data-vd-item]'));
  }

  /**
   * @param {HTMLElement} item
   * @returns {number}
   */
  #getQty(item) {
    const value = parseInt(item.dataset.qty || '0', 10);
    return Number.isFinite(value) ? Math.min(VD_MAX_QTY, Math.max(VD_MIN_QTY, value)) : 0;
  }

  /**
   * @param {HTMLElement} item
   * @param {number} qty
   */
  #setQty(item, qty) {
    const value = Math.min(VD_MAX_QTY, Math.max(VD_MIN_QTY, qty));
    item.dataset.qty = String(value);
  }

  /** @returns {number} */
  #getBackgroundQty() {
    const input = /** @type {HTMLInputElement | null} */ (
      this.#productForm?.querySelector('input[name="quantity"]') ?? null
    );
    const value = parseInt(input?.value || '1', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /** @returns {number | null} The price of the selected background variant in minor units */
  #getBackgroundPrice() {
    const input = /** @type {HTMLInputElement | null} */ (this.#productForm?.querySelector('input[name="id"]') ?? null);
    const id = input?.value;
    if (!id) return null;
    const price = this.#variantPrices[id];
    return typeof price === 'number' ? price : null;
  }

  /** @returns {number} Total number of selected add-on pieces */
  #getDecorCount() {
    return this.#items.reduce((sum, item) => sum + this.#getQty(item), 0);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const toggle = target.closest('[data-vd-toggle]');
    if (toggle instanceof HTMLElement && this.contains(toggle)) {
      event.preventDefault();
      const item = toggle.closest('[data-vd-item]');
      if (item instanceof HTMLElement) this.#toggle(item);
      return;
    }

    const step = target.closest('[data-vd-step]');
    if (step instanceof HTMLButtonElement && this.contains(step)) {
      event.preventDefault();
      if (step.disabled) return;
      const item = step.closest('[data-vd-item]');
      if (!(item instanceof HTMLElement)) return;
      const delta = parseInt(step.dataset.vdStep || '0', 10);
      this.#setQty(item, this.#getQty(item) + delta);
      this.#hideError();
      this.#render();
    }
  };

  /**
   * Accordion type B: opening one row closes all others.
   * @param {HTMLElement} item
   */
  #toggle(item) {
    const willOpen = !item.classList.contains('is-open');

    for (const other of this.#items) {
      const open = other === item ? willOpen : false;
      other.classList.toggle('is-open', open);
      other.querySelector('[data-vd-toggle]')?.setAttribute('aria-expanded', open ? 'true' : 'false');
      const panel = other.querySelector('[data-vd-panel]');
      if (panel instanceof HTMLElement) panel.hidden = !open;
    }
  }

  /**
   * @param {Event & {detail?: {quantity?: number, cartLine?: number}}} event
   */
  #onQuantitySelectorUpdate = (event) => {
    if (event.detail?.cartLine) return;
    const form = this.#productForm;
    if (form && event.target instanceof Node && !form.contains(event.target)) return;
    this.#render();
  };

  /**
   * @param {Event} event
   */
  #onFormChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.name === 'quantity') {
      this.#render();
    }
  };

  /**
   * @param {Event & {detail?: {data?: {productId?: string}}}} event
   */
  #onVariantUpdate = (event) => {
    const productId = event.detail?.data?.productId;
    if (productId && this.dataset.productId && productId !== this.dataset.productId) return;

    // product-form.js updates the variant id and morphs the Add to cart button synchronously in its own
    // listener of this event, so re-apply our state right after all listeners have run.
    if (this.#labelTimeout) clearTimeout(this.#labelTimeout);
    this.#labelTimeout = setTimeout(() => this.#render(), 0);
  };

  #render() {
    const decorCount = this.#getDecorCount();
    let decorTotal = 0;

    for (const item of this.#items) {
      const qty = this.#getQty(item);
      const price = parseInt(item.dataset.price || '0', 10) || 0;
      decorTotal += qty * price;

      const output = item.querySelector('[data-vd-qty]');
      if (output) output.textContent = String(qty);

      const minus = item.querySelector('[data-vd-step="-1"]');
      if (minus instanceof HTMLButtonElement) minus.disabled = qty <= VD_MIN_QTY;
      const plus = item.querySelector('[data-vd-step="1"]');
      if (plus instanceof HTMLButtonElement) plus.disabled = qty >= VD_MAX_QTY;

      const chosen = item.querySelector('[data-vd-chosen]');
      if (chosen instanceof HTMLElement) {
        chosen.hidden = qty === 0;
        chosen.textContent = qty > 0 ? `✓ ${qty}×` : '';
      }
      item.classList.toggle('is-selected', qty > 0);
    }

    // Summary line
    const summary = this.querySelector('[data-vd-summary]');
    const backgroundPrice = this.#getBackgroundPrice();
    if (summary instanceof HTMLElement) {
      if (decorCount > 0 && backgroundPrice !== null) {
        const backgroundTotal = backgroundPrice * this.#getBackgroundQty();
        summary.textContent = '';
        summary.append(
          `${this.dataset.labelBackground} ${this.#formatMoney(backgroundTotal)} + ${this.dataset.labelDecor} ${this.#formatMoney(
            decorTotal
          )} = ${this.dataset.labelTotal} `
        );
        const strong = document.createElement('strong');
        strong.textContent = this.#formatMoney(backgroundTotal + decorTotal);
        summary.append(strong);
        summary.hidden = false;
      } else {
        summary.hidden = true;
        summary.textContent = '';
      }
    }

    // Buy it now (accelerated checkout) would only buy the background – hide it while decor is selected
    this.#productForm?.classList.toggle('vd-addons-active', decorCount > 0);

    this.#applyButtonLabel(decorCount);
  }

  /**
   * Shows the item count on the Add to cart button while decor is selected; restores the original label otherwise.
   * @param {number} decorCount
   */
  #applyButtonLabel(decorCount) {
    const button = /** @type {HTMLButtonElement | null} */ (
      this.#productForm?.querySelector('[ref="addToCartButton"]') ?? null
    );
    const label = /** @type {HTMLElement | null} */ (
      button?.querySelector('.add-to-cart-text__content > span > span') ?? null
    );
    if (!button || !label) return;

    const current = label.textContent || '';
    const isOurs = label.dataset.vdLabel !== undefined && label.dataset.vdLabel === current;

    if (decorCount > 0 && !button.disabled) {
      if (!isOurs) label.dataset.vdOrig = current;
      const count = this.#getBackgroundQty() + decorCount;
      const text = (this.dataset.labelButton || '').replace('[count]', String(count));
      label.textContent = text;
      label.dataset.vdLabel = text;
    } else if (isOurs) {
      label.textContent = label.dataset.vdOrig ?? current;
      delete label.dataset.vdLabel;
      delete label.dataset.vdOrig;
    }
  }

  #hideError() {
    const error = this.querySelector('[data-vd-error]');
    if (error instanceof HTMLElement) {
      error.hidden = true;
      error.textContent = '';
    }
  }

  /* ---------- Public API used by product-form.js ---------- */

  /** @returns {boolean} */
  hasSelection() {
    return this.#getDecorCount() > 0;
  }

  /** @returns {Array<{id: number, quantity: number}>} */
  getCartItems() {
    return this.#items
      .map((item) => ({ id: Number(item.dataset.variantId), quantity: this.#getQty(item) }))
      .filter((line) => line.id > 0 && line.quantity > 0);
  }

  /** @returns {string} A random id that links the background line with its add-on lines */
  createBundleId() {
    const random =
      typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function'
        ? Array.from(crypto.getRandomValues(new Uint32Array(2)), (n) => n.toString(36)).join('')
        : Math.random().toString(36).slice(2);
    return `vd${Date.now().toString(36)}${random}`.slice(0, 32);
  }

  /** Resets all add-on quantities to 0 (after they were added to the cart). */
  reset() {
    for (const item of this.#items) this.#setQty(item, 0);
    this.#hideError();
    this.#render();
  }

  /** Shows the localized error message (add-ons could not be added). */
  showError() {
    const error = this.querySelector('[data-vd-error]');
    if (error instanceof HTMLElement) {
      error.textContent = this.dataset.labelError || '';
      error.hidden = false;
    }
  }
}

if (!customElements.get('vd-addons')) {
  customElements.define('vd-addons', VdAddons);
}
